/**
 * Pairing states as people should read them, keyed by the name the identity service sends.
 * Separate from `messages` because the key is only known at runtime, and an unknown state should
 * fall back to its raw name rather than render as blank.
 */
const pairingStatusLabels: Record<string, string> = {
    Consumed: 'Active',
    Revoked: 'Revoked',
    SignedOut: 'Signed out',
    Superseded: 'Replaced',
    Expired: 'Expired',
    Denied: 'Denied',
    Pending: 'Pending',
    Approved: 'Approved',
};

export const pairingStatusLabel = (status: string): string => pairingStatusLabels[status] || status;

export const messages = {
    'devicePairing.title': 'Pair a device',
    'devicePairing.subtitle': 'Enter the six-character code showing on your headset.',
    'devicePairing.codeLabel': 'Pairing code',
    'devicePairing.action.continue': 'Continue',
    'devicePairing.action.clear': 'Clear code',
    'devicePairing.action.cancel': 'Cancel',
    'devicePairing.action.approve': 'Approve',
    'devicePairing.action.deny': 'Deny',
    'devicePairing.action.done': 'Done',
    'devicePairing.action.pairAnother': 'Pair another device',
    'devicePairing.action.openDialog': 'Enter a pairing code',

    'devicePairing.confirm.title': 'Is this your device?',
    'devicePairing.confirm.subtitle':
    'Approving signs this device in as you, with your assignments, roles and seat.',
    'devicePairing.confirm.warning':
    'If you did not just ask a device to pair, deny this request and tell your administrator.',

    'devicePairing.approved.title': 'Device paired',
    'devicePairing.approved.body': 'Your device is signing in now.',
    'devicePairing.approvedFor.body': (name: string) => `The device is signing in as ${name} now.`,
    'devicePairing.denied.title': 'Request denied',
    'devicePairing.denied.body': 'That device was not signed in.',

    // ---- pairing for somebody else ----

    'devicePairing.pairFor.legend': 'Sign this device in as',
    'devicePairing.pairFor.self': 'Me',
    'devicePairing.pairFor.someoneElse': 'Someone else',
    'devicePairing.pairFor.searchPlaceholder': 'Search by name or email',
    'devicePairing.pairFor.noResults': 'No matching people.',
    'devicePairing.pairFor.searching': 'Searching…',
    'devicePairing.pairFor.change': 'Choose someone else',
    'devicePairing.pairFor.required': 'Choose who this device is for.',
    'devicePairing.confirmFor.title': 'Pair this device for someone?',
    'devicePairing.confirmFor.subtitle': (name: string) =>
        `Approving signs this device in as ${name}, with their assignments, roles and seat.`,
    'devicePairing.confirmFor.warning':
    'They stay signed in on it until somebody revokes it. Only approve a device you are handing to them.',

    'devicePairing.device.unnamed': 'Unnamed device',
    'devicePairing.device.platform': 'Platform',
    'devicePairing.device.version': 'App version',
    'devicePairing.device.ip': 'Requested from',
    'devicePairing.device.requestedAt': 'Requested at',

    'devicePairing.landing.title': 'Pair a device',
    'devicePairing.landing.subtitle':
    'Your headset is waiting. Enter the code it is showing to sign it in as you.',

    'devicePairing.error.format': 'A pairing code is six letters and digits, as shown on the device.',
    'devicePairing.error.invalidCode': 'That code is not valid any more. Ask the device for a new one.',
    'devicePairing.error.userNotFound': 'That person is no longer available. Refresh the list and try again.',
    'devicePairing.error.notAllowedForUser': 'You cannot pair a device for that person.',
    'devicePairing.error.default': 'Please try again or contact your administrator.',

    // ---- the administrator's panel ----

    'managedDevices.title': 'Paired devices',
    'managedDevices.subtitle':
    'Every device signed in by pairing across your company. Revoking one signs that person out of WEAVR Player.',
    'managedDevices.action.pairDevice': 'Pair a device',
    'managedDevices.action.revoke': 'Revoke',
    'managedDevices.searchPlaceholder': 'Search by person, device, device ID or platform',

    'managedDevices.column.user': 'Person',
    'managedDevices.column.device': 'Device',
    'managedDevices.column.platform': 'Platform',
    'managedDevices.column.version': 'App version',
    'managedDevices.column.pairedAt': 'Paired',
    'managedDevices.column.pairedBy': 'Paired by',
    'managedDevices.column.actions': '',

    'managedDevices.pairedBy.self': 'Themselves',
    'managedDevices.unnamed': 'Unnamed device',
    'managedDevices.empty': 'Nobody has paired a device yet.',
    'managedDevices.emptyFiltered': 'No paired devices match that search.',
    'managedDevices.count': (total: number) =>
        total === 1 ? '1 paired device' : `${total} paired devices`,

    'managedDevices.column.status': 'Status',
    'managedDevices.noOwner': '—',

    // ---- history filter and summary (SuperAdmin / MultiAdmin only) ----

    'managedDevices.filter.legend': 'Show',

    'managedDevices.summary.title': 'All pairings',
    'managedDevices.summary.total': 'Total',
    'managedDevices.summary.hint': 'Select a state to filter the list.',

    'managedDevices.emptyForFilter': 'No pairings match that filter.',

    'managedDevices.revokeConfirm.message': (name: string) => `Revoke this device for ${name}?`,
    // The coarseness is a real IdentityServer limitation, not a detail to bury: whoever clicks
    // this needs to know it reaches further than the one device in front of them.
    'managedDevices.revokeConfirm.note':
    'This signs them out of every WEAVR Player device they have paired, and frees their player seat. They can pair again afterwards.',
    'managedDevices.revokeError': 'That device could not be revoked. Please try again.',
}
